// FieldType = 1
import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { useFormikContext, useField } from 'formik';

import { Q1Value } from 'models/documents';
import { AIsuggestion } from 'models/response';

import utils from 'utils';

import { InputsContainer, InputQ1Wrapper, InputLabel, CheckboxContainer, CheckboxLabel } from './styles';

import TextInput from 'components/Input/TextInput';
import Checkbox from 'components/Buttons/CheckboxButton';
import { useSelector } from 'react-redux';
import selectors from 'state/selectors';

const InputsContainerFormula = styled(InputsContainer)`
  margin-right: 248px;
`;

interface Props {
  label: string;
  name: string;
  fixedName: string | undefined;
  formula: string;
  readonly?: boolean;
  val: AIsuggestion[];
  flag: React.MutableRefObject<boolean>;
}

const Formula = (props: Props) => {
  const { name, label, formula, readonly, fixedName, flag } = props;

  const [field, , helpers] = useField<Q1Value>(name);
  const { values, resetForm } = useFormikContext<{ [key: string]: Q1Value }>();

  const decimalScale = useSelector(selectors.documents.selectDocumentDecimal);

  const documentFlatFields = useSelector(selectors.documents.selectDocumentFlatFields);

  const { hTableValues, values: docValues } = documentFlatFields.find(({ fieldName }) => fieldName === 'Valuta') || {};
  const valutaCode = docValues?.[0]?.content;
  const initialDecimals = hTableValues?.find(({ code }) => code === valutaCode)?.decimals;

  useEffect(
    () => {
      if (!readonly) {
        let result: number | boolean | undefined = undefined;
        if (formula.indexOf('=') > -1) {
          result = utils.formula.formulaResultCalculation(formula, values, 1, decimalScale);
          if (result !== undefined) {
            helpers.setValue({
              ...field.value,
              content: [{ ...field.value.content[0], content: result.toString() }],
            });
          }
        } else {
          result = utils.formula.formulaResultCalculation(formula, values, 2, decimalScale);
          if (typeof result === 'number') {
            const resultFormatted = utils.intl.formatNumber(result, decimalScale, ',');
            helpers.setValue({
              ...field.value,
              content: [{ ...field.value.content[0], content: result !== 0 ? resultFormatted.toString() : '' }],
            });
          }
        }

        if (!flag.current) {
          const formVal = values;
          if (fixedName && result !== undefined) {
            const currentResult =
              typeof result === 'number' ? utils.intl.formatNumber(result, initialDecimals, ',') : result;
            const content = [
              {
                ...formVal[fixedName].content[0],
                content: result !== 0 ? currentResult.toString() : '',
              },
            ];
            formVal[fixedName].content = content;
          }
          // Use use resetForm to set the form's dirty status to false
          // Otherwise we would be asked to save data when we try to go back
          resetForm({ values: formVal });
        }
      }
      // Dependencies are built dynamically, based on the formula's fields
      // considering multivalues
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    !readonly
      ? [
          ...formula
            .split(/[=+-]/)
            .map((ele) => values[ele])
            .map((value) => {
              const sum =
                value?.content?.reduce(
                  (accumulator: number, currentElement) =>
                    accumulator + parseFloat(currentElement.content.replace(',', '.') || '0'),
                  0,
                ) ?? 0;
              return sum;
            }),
          decimalScale,
        ]
      : [],
  );

  let value = '';
  try {
    value = typeof field.value.content === 'string' ? field.value.content : field.value.content[0].content;
  } catch (e) {
    console.error(e);
    utils.app.notify('warning', `Field ${name}:${label} received with a non valid value`, 5000);
  }

  return (
    <>
      {formula.indexOf('=') > -1 ? (
        <InputsContainer>
          <CheckboxContainer>
            <CheckboxLabel>{label}</CheckboxLabel>
            <Checkbox name={name} isActive={value ? JSON.parse(value) : false} isEditable={false} />
          </CheckboxContainer>
        </InputsContainer>
      ) : (
        <InputsContainerFormula>
          <InputQ1Wrapper>
            <InputLabel title={label}>{label}</InputLabel>
            <TextInput id={name} value={value} name={name} disabled={true} borderRadius="4px" />
          </InputQ1Wrapper>
        </InputsContainerFormula>
      )}
    </>
  );
};

export default Formula;
